import { MessageSquareShare, ScanText, Search } from 'lucide-react';
import React, { useCallback, useEffect, useState } from "react";
import DataTablePagination from '../../DataTablePagination';
import Spacing from '../../Spacing';
import MessageTemplateDataTable from '../../DataTable/MessageTemplateDataTable';
import MessageTemplateModal from '../../Modal/MessageTemplateModal';

import {
  fetchMessageTemplate,
  loadGenerateTemplate,
} from "../../../services/message";
import {
  generateMessageTemplateTableData,
  notify,
  getPractice,
  numberWithCommas,
  can_visit,
  debounce,
} from "../../../helpers";
import { useDocumentTitle } from '../../../hooks/customDocumentTitleHook';

export default function BulkMessages({selectedPracticeChange, fetchChangePracticeData}) {
  let location_id = getPractice("location_id");
  let account_id = getPractice("account_id");
  if(selectedPracticeChange != null && fetchChangePracticeData){
    location_id = selectedPracticeChange?.location_id;
    account_id = selectedPracticeChange?.account_id;
  }

  const [dataLoading, setDataLoading] = useState(true);
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState({
    page: 1,
    per_page: 10,
    search_term: "",
    template_type: "",
    title: "",
    order_by: [],
  });

  const [dataCount, setDataCount] = useState({
    totalCount: 0,
    totalPages: 0
  });

  const loadData = async (getFilter) => {
    setFilter(getFilter);
    let isCanceled = false;

    try {
      const response = await fetchMessageTemplate(getFilter, account_id);
      if (response) {
        const generateData = generateMessageTemplateTableData(
          response.data.results
        );
        setData(generateData);
        setDataCount({
          totalCount: response.data.count,
          totalPages: response.data.total_pages,
        });
      }
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        isCanceled = true;
      }
    } finally {
      if (!isCanceled) {
        setDataLoading(false);
      }
    }
  };

  useEffect(() => {
    loadData(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const handlePageChange = (newPage) => {
    setFilter({ ...filter, page: newPage });
    setDataLoading(true);
  };

  const debouncedSetFilter = useCallback(
    debounce((newFilter) => setFilter(newFilter), 300), []
  );

  const handleChangeSearchTerm = (event) => {
    const newFilter = { ...filter, search_term: event.target.value, page: 1 };
    setDataLoading(true);
    debouncedSetFilter(newFilter);
  };

  const handleChangeRowsPerPage = (value) => {
    setFilter({
      ...filter,
      page: 1,
      per_page: value,
    });
    setDataLoading(true);
  };

  const handleGenerateTemplate = async () => {
    const response = await loadGenerateTemplate(account_id, location_id);
    if (response) {
      notify("success", response.data);
      loadData(filter);
    }
    setDataLoading(true);
  };

  useEffect(() => {
    const sameLocation = filter.location_id === location_id;
    const sameAccount = filter.account_id === account_id;

    if (sameLocation && sameAccount) return;

    setFilter({
      ...filter,
      location_id: location_id,
      account_id: account_id,
    });
    setDataLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_id, account_id]);

  useDocumentTitle("Message Templates");

  return (
    <>
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24">
          <i
            className="docpro_white_color"
            style={{ backgroundColor: '#898E87' }}
          >
            <MessageSquareShare width={20} height={20} />
          </i>
          Message Templates
          <span>{numberWithCommas(dataCount.totalCount)}</span>
        </h2>
        <div className="docpro_page_heading_btns">
          {(can_visit("messages") && can_visit("messages_message_templates_create")) && (
            <MessageTemplateModal reloadData={loadData} reloadDataFilter={filter} account_id={account_id} editModal={false} />
          )}

          {can_visit("administrator") && (
            <button className="docpro_bordered_btn docpro_fw_600" onClick={handleGenerateTemplate} >
              <ScanText width={20} height={20} />
              <span>Generate Template</span>
            </button>
          )}
        </div>
      </div>
      <Spacing lg="12" sm="12" />
      <div className="docpro_data_table_wrap">
        <div className="docpro_data_table_headings">
          <div className="docpro_data_table_heading_input_group">
            <form className="docpro_search_form">
              <input
                type="text"
                placeholder="Search"
                className="docpro_search_input"
                onChange={handleChangeSearchTerm}
              />
              <button className="docpro_search_btn">
                <Search width={20} height={20} />
              </button>
            </form>
          </div>
        </div>
        <MessageTemplateDataTable
          tableData={data}
          practiceID={account_id}
          reloadData={loadData} 
          reloadDataFilter={filter}
          dataLoading={dataLoading}
          setDataLoading={setDataLoading}
        />        
        {(data.length > 0 && !dataLoading) && (
          <DataTablePagination
            currentPage={filter.page}
            totalPages={dataCount.totalPages}
            onPageChange={handlePageChange}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRecords={dataCount.totalCount}
            currentPageData={filter.per_page}
          />
        )}
      </div>
    </>
  );
}